// src/components/Cards/CardConfiguration/CardConfigEvent/DescargarResultadosPdfModal.jsx
// FTR-0067: descarga de resultados PDF de varios deportes colectivos (fase de grupos, fase final o ambas), con tablas
// opcionales por deporte (posiciones, acumulada, ubicación final; atajos para marcar o quitar cada una en todos) y dos
// diseños: 4:5 para redes (el de comunicaciones, por defecto) o A4 compacto. Solo visible para rol 1 (el backend
// también lo exige). Igual que la descarga masiva de
// programación, lista los deportes con /menu_get_sports; pero pide un PDF por deporte a POST /resultados-fases-pdf
// para mostrar el avance real y, si son varios, arma el ZIP en el navegador.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "@mui/joy/Modal";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import ModalClose from "@mui/joy/ModalClose";
import { toast } from "react-hot-toast";
import { getMenuSportsForEvent } from "../../../../services/menuSportsService.js";
import {
  downloadResultadosFasesPdf,
  isCancelledRequest,
  omittedLabel,
  resultadosPdfErrorMessage,
} from "../../../../services/resultadosFasesPdfService.js";
import { buildZipBlob } from "../../../../utils/zipStore.js";

const COLLECTIVE_TYPE_SPORT = 1;
const BRAND = { navy: "#1A3358", red: "#E71D2E", blue: "#2564eb" };

export const RESULT_DESIGNS = [
  {
    value: "social",
    title: "Redes sociales",
    badge: "Por defecto",
    description: "Páginas 4:5 con tarjetas grandes y la marca del evento, como el PDF de resultados para comunicaciones.",
  },
  {
    value: "compact",
    title: "Reporte compacto",
    description: "A4 sobrio con los mismos recursos gráficos: entran muchos más resultados por página.",
  },
];

export const RESULT_EXTRAS = [
  { key: "standings", label: "Tabla de posiciones", short: "Posiciones" },
  { key: "accumulated", label: "Tabla acumulada", short: "Acumulada" },
  { key: "finalStandings", label: "Ubicación final", short: "Ubicación final" },
];

/** Tiempo esperado por deporte antes de medir el primero (solo anima la barra mientras llega el PDF). */
const EXPECTED_MS = { social: 10000, compact: 7000 }; // medido con los 8 colectivos del 1001 (FTR-0067)

const emptySelection = () => ({ groups: false, finals: false });
/** Tablas de un deporte que aún no se tocó: todas marcadas (las que no existen se omiten con aviso). */
const defaultTables = () => ({ standings: true, accumulated: true, finalStandings: true });
const pad = (n) => String(n).padStart(2, "0");

export const zipFilenameFor = (idevent, date = new Date()) =>
  `resultados_evento_${idevent}_${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(
    date.getHours()
  )}-${pad(date.getMinutes())}.zip`;

const phasesLabel = ({ groups, finals }) => (groups && finals ? "Grupos + final" : groups ? "Fase de grupos" : "Fase final");

const includedTables = (include) => RESULT_EXTRAS.filter((extra) => include?.[extra.key]);

const tablesLabel = (include) => {
  const count = includedTables(include).length;
  return count ? ` + ${count} ${count === 1 ? "tabla" : "tablas"}` : "";
};

const formatSeconds = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  return total < 60 ? `${total} s` : `${Math.floor(total / 60)} min ${pad(total % 60)} s`;
};

const formatSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Avance total (0..1): deportes terminados + el actual, que se acerca al 92 % según el tiempo esperado (promedio
 * de los ya generados). Nunca llega solo al 100 %: eso lo marca la respuesta del servidor.
 */
export function overallProgress({ items, now, expectedMs }) {
  const total = items.length || 1;
  const finished = items.filter((item) => item.status === "done" || item.status === "error").length;
  const working = items.find((item) => item.status === "working");
  let partial = 0;
  if (working?.startedAt) {
    const ratio = Math.max(0, now - working.startedAt) / Math.max(expectedMs, 1);
    partial = 0.92 * (1 - Math.exp(-2.2 * ratio));
  }
  return Math.min(1, (finished + partial) / total);
}

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const StatusIcon = ({ status }) => {
  if (status === "done") {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[11px] font-black text-white">
        ✓
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[11px] font-black text-white">
        ✕
      </span>
    );
  }
  if (status === "working") {
    return <span className="h-5 w-5 shrink-0 animate-spin rounded-full border-[3px] border-blue-200 border-t-blue-600" />;
  }
  return <span className="h-5 w-5 shrink-0 rounded-full border-2 border-dashed border-slate-300" />;
};

/** Casilla con estado intermedio (algunos deportes sí, otros no): el atributo `indeterminate` solo existe por JS. */
const TriStateCheckbox = ({ checked, indeterminate, ...props }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} {...props} />;
};

/** Miniatura del diseño (solo ilustrativa). */
const DesignPreview = ({ value }) =>
  value === "compact" ? (
    <div className="flex h-[68px] w-12 shrink-0 flex-col overflow-hidden rounded border border-slate-300 bg-white shadow-sm">
      <div className="h-2.5 bg-white" style={{ borderBottom: `2px solid ${BRAND.red}` }} />
      <div className="mx-1 mt-1 h-1 rounded-sm" style={{ background: BRAND.navy }} />
      {Array.from({ length: 9 }, (_, i) => (
        <div key={i} className="mx-1 mt-[3px] h-[3px] rounded-sm bg-slate-300" />
      ))}
      <div className="mt-auto h-1.5" style={{ background: BRAND.navy }} />
    </div>
  ) : (
    <div className="flex h-[68px] w-[55px] shrink-0 flex-col overflow-hidden rounded shadow-sm" style={{ background: BRAND.navy }}>
      <div className="h-3.5 bg-white" style={{ borderBottom: `3px solid ${BRAND.red}` }} />
      <div className="mx-1.5 mt-1.5 h-3.5 rounded bg-white" />
      <div className="mx-1.5 mt-1 h-3.5 rounded bg-white" />
      <div className="mx-1.5 mt-1 h-3.5 rounded bg-white" />
    </div>
  );

const DescargarResultadosPdfModal = ({ open, onClose, idevent }) => {
  const { user } = useSelector((state) => state.auth);

  const [sports, setSports] = useState([]);
  const [loadingSports, setLoadingSports] = useState(false);
  const [selections, setSelections] = useState({}); // idsport -> { groups, finals }
  const [tables, setTables] = useState({}); // idsport -> { standings, accumulated, finalStandings }; sin tocar = defaultTables()
  const [design, setDesign] = useState("social");
  const [job, setJob] = useState(null); // { phase, items, startedAt, design, downloaded }
  const [now, setNow] = useState(Date.now());
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open || !idevent || !user?.iduser) return;
    let cancelled = false;

    setLoadingSports(true);
    setSelections({});
    setTables({});
    setJob(null);

    getMenuSportsForEvent({ iduser: user.iduser, idevent })
      .then((allSports) => {
        if (cancelled) return;
        setSports((allSports || []).filter((sport) => Number(sport.typeSport) === COLLECTIVE_TYPE_SPORT));
      })
      .catch((error) => {
        console.error("Error cargando deportes del evento:", error);
        if (!cancelled) toast.error("No se pudieron cargar los deportes del evento.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSports(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, idevent, user?.iduser]);

  // Cancela el pedido en curso si el modal se desmonta.
  useEffect(() => () => abortRef.current?.abort(), []);

  const running = job?.phase === "running" || job?.phase === "zipping";

  useEffect(() => {
    if (job?.phase !== "running") return undefined;
    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, [job?.phase]);

  const selectedSports = sports.filter((sport) => {
    const sel = selections[sport.idsport];
    return sel?.groups || sel?.finals;
  });
  const selectedCount = selectedSports.length;

  const allSelected =
    sports.length > 0 &&
    sports.every((sport) => {
      const sel = selections[sport.idsport];
      return sel?.groups && sel?.finals;
    });

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelections({});
      return;
    }
    const next = {};
    sports.forEach((sport) => {
      next[sport.idsport] = { groups: true, finals: true };
    });
    setSelections(next);
  };

  const toggleSportPhase = (idsport, phase) => {
    setSelections((prev) => {
      const current = prev[idsport] || emptySelection();
      return { ...prev, [idsport]: { ...current, [phase]: !current[phase] } };
    });
  };

  const toggleSportParent = (idsport) => {
    setSelections((prev) => {
      const current = prev[idsport] || emptySelection();
      const next = current.groups || current.finals ? emptySelection() : { groups: true, finals: true };
      return { ...prev, [idsport]: next };
    });
  };

  const tablesOf = (idsport) => tables[idsport] || defaultTables();

  const toggleSportTable = (idsport, key) => {
    setTables((prev) => {
      const current = prev[idsport] || defaultTables();
      return { ...prev, [idsport]: { ...current, [key]: !current[key] } };
    });
  };

  // Los atajos miran los deportes elegidos (lo que se va a descargar) o, si aún no hay ninguno, todos; y marcan o
  // quitan la tabla en todos los deportes, para que un deporte elegido después ya la traiga igual.
  const shortcutScope = selectedSports.length ? selectedSports : sports;
  const shortcutState = (key) => {
    const marked = shortcutScope.filter((sport) => tablesOf(sport.idsport)[key]).length;
    return { marked, total: shortcutScope.length, all: marked > 0 && marked === shortcutScope.length };
  };

  const setTableForAll = (key, value) => {
    setTables((prev) => {
      const next = { ...prev };
      sports.forEach((sport) => {
        next[sport.idsport] = { ...(prev[sport.idsport] || defaultTables()), [key]: value };
      });
      return next;
    });
  };

  const closeIfIdle = () => {
    if (running) return;
    onClose?.();
  };

  const patchItem = (index, patch) =>
    setJob((prev) => (prev ? { ...prev, items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) } : prev));

  const handleDownload = async () => {
    const chosen = sports
      .map((sport) => {
        const sel = selections[sport.idsport];
        if (!sel || (!sel.groups && !sel.finals)) return null;
        return {
          idsport: Number(sport.idsport),
          name: sport.displayName || sport.name,
          groups: !!sel.groups,
          finals: !!sel.finals,
          include: { ...tablesOf(sport.idsport) },
        };
      })
      .filter(Boolean);

    if (chosen.length === 0) {
      toast("Selecciona al menos un deporte y una fase.", { icon: "⚠️" });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const startedAt = Date.now();
    setNow(startedAt);
    setJob({ phase: "running", startedAt, design, items: chosen.map((sport) => ({ ...sport, status: "pending" })) });

    const files = [];
    let errors = 0;
    for (let index = 0; index < chosen.length; index += 1) {
      if (controller.signal.aborted) break;
      const sport = chosen[index];
      const itemStartedAt = Date.now();
      patchItem(index, { status: "working", startedAt: itemStartedAt });
      try {
        const pdf = await downloadResultadosFasesPdf({
          idevent,
          idsport: sport.idsport,
          groups: sport.groups,
          finals: sport.finals,
          include: sport.include,
          design,
          signal: controller.signal,
        });
        files.push({ name: pdf.filename, data: pdf.data });
        patchItem(index, { status: "done", ms: Date.now() - itemStartedAt, omitted: pdf.omitted, bytes: pdf.data.length });
      } catch (error) {
        if (controller.signal.aborted || isCancelledRequest(error)) break;
        errors += 1;
        console.error(`Error generando los resultados de ${sport.name}:`, error);
        patchItem(index, { status: "error", error: resultadosPdfErrorMessage(error) });
      }
    }
    abortRef.current = null;

    if (controller.signal.aborted) {
      setJob((prev) => prev && { ...prev, phase: "cancelled", items: prev.items.map((item) => (item.status === "working" || item.status === "pending" ? { ...item, status: "cancelled" } : item)) });
      toast("Descarga cancelada.", { icon: "⏹️" });
      return;
    }

    if (files.length === 0) {
      setJob((prev) => prev && { ...prev, phase: "failed" });
      toast.error("No se pudo generar ningún PDF de resultados.");
      return;
    }

    let downloaded;
    if (files.length === 1) {
      downloaded = files[0].name;
      triggerDownload(new Blob([files[0].data], { type: "application/pdf" }), downloaded);
    } else {
      setJob((prev) => prev && { ...prev, phase: "zipping" });
      // deja pintar "Empaquetando ZIP…" antes de armarlo (es síncrono)
      await new Promise((resolve) => setTimeout(resolve, 50));
      downloaded = zipFilenameFor(idevent);
      triggerDownload(buildZipBlob(files), downloaded);
    }
    setJob((prev) => prev && { ...prev, phase: "done", downloaded, finishedAt: Date.now() });
    if (errors > 0) toast.error(`Se descargaron ${files.length} PDF; ${errors} no se pudieron generar.`);
    else toast.success(files.length === 1 ? "Resultados generados correctamente." : `ZIP con ${files.length} PDF generado correctamente.`);
  };

  const cancelDownload = () => abortRef.current?.abort();

  const renderProgress = () => {
    const items = job.items;
    const finishedMs = items.filter((item) => item.status === "done" && item.ms).map((item) => item.ms);
    const expectedMs = finishedMs.length
      ? finishedMs.reduce((sum, ms) => sum + ms, 0) / finishedMs.length
      : EXPECTED_MS[job.design] || EXPECTED_MS.social;
    const progress = overallProgress({ items, now, expectedMs });
    const pct =
      job.phase === "done" ? 100 : job.phase === "zipping" ? 98 : job.phase === "running" ? Math.floor(progress * 97) : Math.floor(progress * 100);
    const working = items.find((item) => item.status === "working");
    const finished = items.filter((item) => item.status === "done" || item.status === "error").length;
    const headline = {
      running: working ? `Generando ${working.name}…` : "Preparando…",
      zipping: "Empaquetando ZIP…",
      done: "¡Descarga lista!",
      cancelled: "Descarga cancelada",
      failed: "No se pudo generar ningún PDF",
    }[job.phase];
    const elapsed = (job.finishedAt || now) - job.startedAt;
    const failedBar = job.phase === "failed" || job.phase === "cancelled";

    return (
      <div data-testid="resultados-pdf-progress">
        <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-white p-5 shadow-sm">
          <div className="flex items-baseline justify-between gap-3">
            <p className="min-w-0 truncate text-sm font-black text-slate-800">{headline}</p>
            <span className="shrink-0 text-3xl font-black tabular-nums" style={{ color: failedBar ? "#94a3b8" : BRAND.navy }}>
              {pct}%
            </span>
          </div>
          <div
            className="mt-3 h-3.5 w-full overflow-hidden rounded-full bg-slate-200"
            role="progressbar"
            aria-label="Progreso de la descarga de resultados"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
          >
            <div
              className="relative h-full overflow-hidden rounded-full"
              style={{
                width: `${pct}%`,
                transition: "width 350ms ease-out",
                background: failedBar
                  ? "#94a3b8"
                  : `linear-gradient(90deg, ${BRAND.navy} 0%, ${BRAND.blue} 60%, ${BRAND.red} 100%)`,
              }}
            >
              {running ? (
                <div
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,0.22) 0 10px, rgba(255,255,255,0) 10px 20px)",
                  }}
                />
              ) : null}
            </div>
          </div>
          <p className="mt-2 text-xs font-semibold text-slate-500">
            {finished} de {items.length} {items.length === 1 ? "deporte" : "deportes"} · {formatSeconds(elapsed)}
            {job.phase === "done" && job.downloaded ? ` · ${job.downloaded}` : ""}
          </p>
        </div>

        <ul className="mt-4 max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {items.map((item) => (
            <li
              key={item.idsport}
              data-testid={`resultados-pdf-item-${item.idsport}`}
              className={`rounded-lg border px-3 py-2 ${
                item.status === "working"
                  ? "border-blue-300 bg-blue-50"
                  : item.status === "error"
                  ? "border-red-200 bg-red-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <StatusIcon status={item.status} />
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{item.name}</span>
                <span
                  className="shrink-0 text-[11px] font-semibold text-slate-400"
                  title={includedTables(item.include).map((extra) => extra.label).join(", ") || "Sin tablas"}
                >
                  {phasesLabel(item)}
                  {tablesLabel(item.include)}
                </span>
                <span className="w-24 shrink-0 text-right text-[11px] font-bold text-slate-500">
                  {item.status === "done"
                    ? `${formatSize(item.bytes || 0)} · ${formatSeconds(item.ms || 0)}`
                    : item.status === "working"
                    ? "Generando…"
                    : item.status === "error"
                    ? "Error"
                    : item.status === "cancelled"
                    ? "Cancelado"
                    : "En cola"}
                </span>
              </div>
              {item.error ? <p className="ml-7 mt-1 text-xs font-semibold text-red-600">{item.error}</p> : null}
              {item.omitted?.length ? (
                <p className="ml-7 mt-1 text-xs font-semibold text-amber-700">{item.omitted.map(omittedLabel).join(" · ")}</p>
              ) : null}
            </li>
          ))}
        </ul>

        <div className="mt-5 flex justify-end gap-2">
          {running ? (
            <button
              type="button"
              onClick={cancelDownload}
              disabled={job.phase === "zipping"}
              className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar descarga
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setJob(null)}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Volver
              </button>
              <button
                type="button"
                onClick={closeIfIdle}
                className="inline-flex items-center justify-center rounded-md bg-[#2564eb] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:brightness-95"
              >
                Cerrar
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <>
      {loadingSports ? (
        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700">
          Cargando deportes...
        </div>
      ) : sports.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600">
          Este evento no tiene deportes colectivos configurados.
        </div>
      ) : (
        <>
          <label className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Seleccionar todos (deportes y fases)
          </label>

          <fieldset className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 pb-2.5 pt-1">
            <legend className="px-1 text-xs font-black uppercase tracking-wide text-slate-500">
              Tablas en todos los deportes
            </legend>
            <div className="flex flex-wrap gap-2">
              {RESULT_EXTRAS.map((extra) => {
                const state = shortcutState(extra.key);
                return (
                  <label
                    key={extra.key}
                    className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                      state.marked > 0 ? "border-blue-400 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600"
                    }`}
                  >
                    <TriStateCheckbox
                      checked={state.all}
                      indeterminate={state.marked > 0 && !state.all}
                      onChange={() => setTableForAll(extra.key, !state.all)}
                      aria-label={`${extra.label} en todos los deportes`}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    {extra.label}
                    <span className="rounded-full bg-white/80 px-1.5 text-[10px] font-black tabular-nums text-slate-500">
                      {state.marked}/{state.total}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] font-medium leading-snug text-slate-400">
              Atajos para marcar o quitar una tabla en todos; en cada deporte puedes ajustarla aparte. Se agregan al PDF
              cuando existen: la acumulada necesita la tabla acumulada nueva y dos o más grupos; la ubicación final, que
              la disciplina ya esté cerrada.
            </p>
          </fieldset>

          <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {sports.map((sport) => {
              const sel = selections[sport.idsport] || emptySelection();
              const sportSelected = sel.groups || sel.finals;
              const name = sport.displayName || sport.name;
              const sportTables = tablesOf(sport.idsport);

              return (
                <div
                  key={sport.idsport}
                  className={`rounded-lg border px-3 py-2 ${
                    sportSelected ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"
                  }`}
                >
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-bold text-slate-800">
                    <input
                      type="checkbox"
                      checked={sportSelected}
                      onChange={() => toggleSportParent(sport.idsport)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    {name}
                  </label>

                  {[
                    {
                      title: "Fases",
                      options: [
                        { key: "groups", label: "Fase de grupos", aria: "Fase de grupos", checked: sel.groups },
                        { key: "finals", label: "Fase final", aria: "Fase final", checked: sel.finals },
                      ],
                      toggle: (key) => toggleSportPhase(sport.idsport, key),
                    },
                    {
                      title: "Tablas",
                      options: RESULT_EXTRAS.map((extra) => ({
                        key: extra.key,
                        label: extra.short,
                        aria: extra.label,
                        checked: sportTables[extra.key],
                      })),
                      toggle: (key) => toggleSportTable(sport.idsport, key),
                    },
                  ].map((group) => (
                    <div key={group.title} className="ml-6 mt-1 flex items-start gap-2">
                      <span className="w-11 shrink-0 pt-px text-[10px] font-black uppercase tracking-wide text-slate-400">
                        {group.title}
                      </span>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {group.options.map((option) => (
                          <label
                            key={option.key}
                            className={`flex cursor-pointer items-center gap-1.5 text-xs font-semibold ${
                              sportSelected ? "text-slate-700" : "text-slate-400"
                            }`}
                          >
                            <input
                              type="checkbox"
                              aria-label={`${option.aria} de ${name}`}
                              checked={option.checked}
                              onChange={() => group.toggle(option.key)}
                              className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <fieldset className="mt-4">
            <legend className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">Diseño</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {RESULT_DESIGNS.map((option) => {
                const checked = design === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3 transition ${
                      checked ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="resultados-pdf-design"
                      value={option.value}
                      checked={checked}
                      onChange={() => setDesign(option.value)}
                      className="sr-only"
                    />
                    <DesignPreview value={option.value} />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-1.5 text-sm font-black text-slate-800">
                        {option.title}
                        {option.badge ? (
                          <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-white">
                            {option.badge}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-xs font-medium leading-snug text-slate-500">
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-slate-400">
          {selectedCount > 0
            ? `${selectedCount} ${selectedCount === 1 ? "deporte: 1 PDF" : `deportes: ${selectedCount} PDF en un ZIP`}`
            : ""}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={closeIfIdle}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={selectedCount === 0 || loadingSports}
            className="inline-flex items-center justify-center rounded-md bg-[#2564eb] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Descargar
          </button>
        </div>
      </div>
    </>
  );

  return (
    <Modal
      aria-labelledby="descargar-resultados-pdf-title"
      open={open}
      onClose={closeIfIdle}
      className="flex items-center justify-center p-4"
    >
      <Sheet
        variant="outlined"
        className="w-full max-w-2xl rounded-md bg-white p-6 shadow-lg"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        {running ? null : <ModalClose variant="plain" className="m-1" onClick={closeIfIdle} />}

        <Typography
          component="h2"
          id="descargar-resultados-pdf-title"
          level="h4"
          className="mb-1 text-xl font-black text-slate-900"
        >
          Descargar resultados PDF
        </Typography>
        <p className="mb-4 text-sm font-medium text-slate-500">
          {job
            ? "Se genera un PDF por deporte; si son varios, se descargan juntos en un ZIP."
            : "Selecciona los deportes colectivos, sus fases y las tablas de cada uno. Cada deporte genera un solo PDF (grupos + final se combinan en uno); si son varios PDFs, se descargan en un ZIP."}
        </p>

        {job ? renderProgress() : renderForm()}
      </Sheet>
    </Modal>
  );
};

export default DescargarResultadosPdfModal;
